import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  ArchiveRestore,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useState } from 'react';
import { ApplicationAPI, Application } from '../api/applications';

interface RestoreApplicationProps {
  isOpen: boolean;
  onClose: () => void;
  application: Application | null;
  onSuccess?: () => void;
}

export function RestoreApplication({ isOpen, onClose, application, onSuccess }: RestoreApplicationProps) {
  const [isRestoring, setIsRestoring] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRestore = async () => {
    if (!application) return;

    setIsRestoring(true);
    setError(null);

    try {
      const response = await ApplicationAPI.restore(application.id);

      if (response.code === '200') {
        setIsRestoring(false);
        setIsComplete(true);

        if (onSuccess) {
          onSuccess();
        }

        onClose();
        setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to restore application');
      }
    } catch (error) {
      setIsRestoring(false);
      setError(error instanceof Error ? error.message : 'Failed to restore application. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Application Restored Successfully!</h3>
          <p className="text-muted-foreground">
            The application "{application?.name}" has been restored and is now active.
          </p>
        </div>
      );
    }

    if (isRestoring) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Restoring Application...</h3>
          <p className="text-muted-foreground">Moving application back to active state...</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-muted-foreground">
              Are you sure you want to restore the application <strong>{application?.name}</strong>?
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <ArchiveRestore className="w-5 h-5 text-blue-600" />
            <span>Restore Application</span>
          </DialogTitle>
          <DialogDescription>
            This action will restore the application and make it active again.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {renderContent()}
        </div>

        {!isRestoring && !isComplete && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRestore}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Restore Application
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}